import moment from "moment";
const LOGFILE_DATE_FORMAT = `YYYYMMDD`;
const LOG_ENTRY_DATE_FORMAT = `YYYY-MM-DD HH:mm:ss.SSSSS`;
const MYSQL_DATETIME_FORMAT = `YYYY-MM-DD HH:mm:ss`;
const MYSQL_DATE_FORMAT = `YYYY-MM-DD`;
const LOG_FOLDER = "log";
const DEFAULT_DATE = new Date(1970, 0, 1, 0, 0, 0, 0);
const DEFAULT_NUMBER = -1;
const DEBOUNCE_TIME = 1500;

const toasterStyle = {
  maxWidth: "50vw",
  backgroundColor: "#eee",
  border: "1px solid #d3d3d3",
  // position: "fixed",
  // top: "50px",
  // opacity: 1,
};

const pageSizeArray = [15, 30, 50, 100, 150, 200];

function strToNumber(str: string, defaultValue = DEFAULT_NUMBER) {
  let result = null;
  if (!str || Number.isNaN(Number(str))) {
    return defaultValue;
  }
  result = Number(str);
  return result;
}

function stringIsNullOrEmpty(str: string | null | undefined) {
  if (str === null || str === undefined || typeof str !== "string") {
    return true;
  }
  if (str.trim().length === 0) {
    return true;
  }
  return false;
}

function integerTryParse(str: string): number | undefined {
  if (str === null || str === undefined) {
    return undefined;
  }
  if (str.trim().length === 0) {
    return undefined;
  }
  const trimmed = str.trim();
  if (Number.isNaN(Number(trimmed))) {
    return undefined;
  }

  if (!Number.isInteger(Number(trimmed))) {
    return undefined;
  }
  return Number(trimmed);
}

function booleanTryParse(str: string): boolean | undefined {
  if (str === null || str === undefined) {
    return undefined;
  }
  if (str.trim().length === 0) {
    return undefined;
  }
  const lowerCase = str.trim().toLowerCase();
  if (lowerCase === "true") {
    return true;
  }
  if (lowerCase === "true") {
    return false;
  }
  return undefined;
}

function stringToNumberArray(str: string) {
  const numArray: number[] = [];
  if (typeof str !== "string") {
    return numArray;
  }
  const strArray = str.split(",");
  if (strArray.length >= 1) {
    for (const item of strArray) {
      const num = Number(item.toString());
      if (isNaN(num)) {
        continue;
      }
      numArray.push(num);
    }
  }
  return numArray;
}

function stringToDateArray(str: string) {
  const dateArray: Date[] = [];
  if (typeof str !== "string") {
    return dateArray;
  }
  const strArray = str.split(",");
  if (strArray.length >= 1) {
    for (const item of strArray) {
      const date = moment(item.toString());
      if (!date.isValid()) {
        continue;
      }
      dateArray.push(date.toDate());
    }
  }
  return dateArray;
}

function dateRangeStringToDateArray(str: string) {
  const dateRangeArray: Date[] = [];
  if (typeof str !== "string") {
    return dateRangeArray;
  }
  const rangeArray = str.split(",");
  if (rangeArray.length >= 1) {
    for (const item of rangeArray) {
      const date = new Date(item.toString());
      if (date === null || isNaN(date.getTime())) {
        continue;
      }
      dateRangeArray.push(date);
    }
  }
  return dateRangeArray;
}

function dateStringToDate(str: string) {
  const date = moment(str.toString());
  if (!date.isValid()) {
    return false;
  }
  return date.toDate();
}

function jsDateToMySqlDateStr(jsDate: Date | string) {
  if (jsDate === "Invalid Date") {
    return null;
  }
  if (!moment(jsDate).isValid()) {
    return null;
  }
  return moment(jsDate).format(MYSQL_DATETIME_FORMAT);
}

const diffDays = (start: string | Date, end: string | Date) => {
  const startDate = moment(start).startOf("day");
  const endDate = moment(end).startOf("day");
  return endDate.diff(startDate, "days");
};

function removeUndefinedProperty(obj: any, removeProp : string[] = []) {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined || removeProp.includes(key)) {
      delete obj[key];
    }
  });
  return obj;
}

function translateArrayToCustomTypeArray(typeString: string, arr: any[]) {
  switch (typeString) {
    case "date":
      return arr.map((item) => new Date(item));
    case "number":
      return arr.map((item) => Number(item));
    case "string":
      return arr.map((item) => String(item));
    case "boolean":
      return arr.map((item) => Boolean(item));
    default:
      return arr;
  }
}

function tryParseJson(jsonStr: string) {
  let obj = null;
  try {
    obj = JSON.parse(jsonStr);
  } catch {
    return false;
  }
  return obj;
}

function dateToUTCDate(date: Date) {
  return moment(date).utc().toDate();
}

function arraysEqual(arr1: any[], arr2: any[]) {
  return (
    arr1.length === arr2.length &&
    arr1.every((value, index) => value === arr2[index])
  );
}

const getDateTimeLocalValue = (jsdate: Date | string) => {
  if (!jsdate || !moment(jsdate).isValid()) {
    return "";
  }
  const tempDate = new Date(moment(jsdate).toDate().toISOString());
  tempDate.setMinutes(tempDate.getMinutes() - tempDate.getTimezoneOffset());
  return tempDate.toISOString().slice(0, 16);
};

function getPageCount(total: number, pageSize: number) {
  if (!total || Number.isNaN(total.toString())) {
    return 0;
  }
  if (!pageSize || Number.isNaN(pageSize.toString())) {
    return 0;
  }
  const div = total / pageSize;
  const pageCountInt = total > 0 ? Math.floor(div) : 1;
  const pageCountRem = total > 0 ? (total % pageSize > 0 ? 1 : 0) : 0;
  return pageCountInt + pageCountRem;
}

function isPaginatedRequest(requestObj: any): boolean {
  if (!requestObj) {
    return false;
  }
  if (
    requestObj.page !== undefined &&
    requestObj.pageSize !== undefined &&
    Number.isInteger(Number(requestObj.page)) &&
    Number.isInteger(Number(requestObj.pageSize))
  ) {
    return true;
  }
  return false;
}

const common = {
  strToNumber,
  dateStringToDate,
  diffDays,
  stringIsNullOrEmpty,
  removeUndefinedProperty,
  dateToUTCDate,
  stringToNumberArray,
  stringToDateArray,
  booleanTryParse,
  integerTryParse,
  tryParseJson,
  arraysEqual,
  getDateTimeLocalValue,
  toasterStyle,
  pageSizeArray,
  getPageCount,
  DEBOUNCE_TIME,
  isPaginatedRequest,
};
export default common;
